import React from "react";

/**
 * ChartNarrative receives three key mix objects and shows a business‑friendly summary.
 *
 * @param {Object} props
 * @param {{ cost: number, co2: number }} props.cheapest   – “Lowest Cost” mix
 * @param {{ cost: number, co2: number }} props.lowCarbon  – “Lowest Emission” mix
 * @param {{ cost: number, co2: number }} props.priciest   – “Highest Cost” mix
 */
function BarChartNarrative({ cheapest, lowCarbon, priciest }) {
  // Derived figures for the narrative.
  const costPremium = lowCarbon.cost - cheapest.cost;
  const emissionReduction = cheapest.co2 - lowCarbon.co2;
  const premiumPct = (costPremium / cheapest.cost) * 100;
  const emissionPctDrop = (emissionReduction / cheapest.co2) * 100;
  const abatementPerThousand = (emissionReduction / costPremium) * 1000;

  const extraCostWorst = priciest.cost - cheapest.cost;
  const extraEmissionWorst = priciest.co2 - cheapest.co2;
  const costMultiplier = (priciest.cost / cheapest.cost).toFixed(1);
  const co2Multiplier = (priciest.co2 / cheapest.co2).toFixed(1);

  return (
    <section className="prose mt-6 max-w-none">
      <h3>Executive insight — Cost&nbsp;vs Carbon for your concrete mixes</h3>

      <p>
        <strong>{premiumPct.toFixed(0)} % premium cuts emissions by {emissionPctDrop.toFixed(0)} %</strong>
        <br />
        Upgrading from the rock‑bottom bid (<b>${cheapest.cost.toLocaleString()} k</b>, {cheapest.co2} t CO₂‑eq)
        to the low‑carbon mix (<b>${lowCarbon.cost.toLocaleString()} k</b>) costs
        <b> +${costPremium.toLocaleString()} k</b> but avoids
        <b> {emissionReduction} t CO₂‑eq</b>—≈ <b>{abatementPerThousand.toFixed(0)} kg CO₂</b> per extra&nbsp;$1 000.
      </p>

      <p>
        <strong>The “premium” option is a money burner, not a climate win</strong>
        <br />
        It hits <b>${priciest.cost.toLocaleString()} k</b> and emits <b>{priciest.co2} t CO₂‑eq</b> — {costMultiplier}×
        the cost and {co2Multiplier}× the carbon of the budget mix.
        Paying <b>${extraCostWorst.toLocaleString()} k</b> more <i>adds</i> <b>{extraEmissionWorst} t</b> of emissions.
      </p>

      <p>
        <strong>Smart choice:</strong> skip the high‑cost/high‑carbon outlier and redirect just&nbsp;
        {premiumPct.toFixed(0)} % of your budget head‑room toward the low‑carbon mix to hit targets at minimal cost impact.
      </p>
    </section>
  );
}

export default BarChartNarrative;
